import React from 'react';
import moment from 'moment';
import { useSelector } from 'react-redux';
import { FormattedMessage, useIntl } from 'react-intl';
import TStore from '../../../../types/store';
import { TGeneralInfo } from '../../../../types/life-id';
import { TPatientDetail } from '../../../../types/patients';
import { NullableString, NullableNumber } from '../../../../types/common';
import { useParams } from 'react-router-dom';
import EnvService from '@services/internal/env';

const HILBI_USER_ID_PROD = 'a912ee85-ec87-4acc-8127-1815f6ff5b47';

const renderSex = (sex: NullableString) => {
  if (sex) {
    return <FormattedMessage id={`sex.${sex.toLowerCase()}`} />;
  }
  return '-';
};

const renderAge = (birthDate: NullableString) => {
  if (birthDate) {
    return (
      <FormattedMessage
        id="profile.years"
        values={{ n: `${moment().diff(moment(birthDate), 'years')}` }}
      />
    );
  }
  return '-';
};

const renderInsurancePin = (pin: string | null) => {
  if (pin === null) return '-';

  return pin;
};

const renderBloodType = (bloodType: NullableString, rhBloodType: NullableString) => {
  if (bloodType) {
    return bloodType;
  }

  if (bloodType && rhBloodType) {
    return `${bloodType}${rhBloodType}`;
  }
  return '-';
};

const renderKids = (kidsCount: NullableNumber) => {
  let kidsMessage = null;

  if (kidsCount === 1) {
    kidsMessage = <FormattedMessage id="profile.n_kids_one" values={{ n: kidsCount }} />;
  } else if (kidsCount && kidsCount > 1 && kidsCount < 5) {
    kidsMessage = <FormattedMessage id="profile.n_kids" values={{ n: kidsCount }} />;
  } else if (kidsCount && kidsCount >= 5) {
    kidsMessage = <FormattedMessage id="profile.n_kids_more" values={{ n: kidsCount }} />;
  } else if (kidsCount && kidsCount === 0) {
    kidsMessage = '';
  }

  return kidsMessage;
};

const hilbiUserCheck = (userId: string | undefined, f: () => string) =>
  userId !== HILBI_USER_ID_PROD && userId ? f() : '-';

const BaseInfoLifeWrapper: React.FC = () => {
  const params = useParams<{ userId: string; jabberId: string }>();

  if (params.userId === EnvService.getSnapshot().xmpp.hilbiJid) {
    return null;
  }
  return <BaseInfoLife />;
};

const BaseInfoLife: React.FC = () => {
  const generalInfo: TGeneralInfo | null = useSelector((store: TStore) => store.lifeId.generalInfo);
  const intl = useIntl();
  const patientDetail: TPatientDetail | null = useSelector(
    (store: TStore) => store.patients.patientDetail,
  );

  if (patientDetail === null) {
    return null;
  }

  const renderSmoker = (smoker: null | boolean) => {
    if (smoker === null) return '-';
    return smoker
      ? intl.formatMessage({ id: 'common:yes' })
      : intl.formatMessage({ id: 'common:no' });
  };

  const renderInsuranceProvider = (provider: string | null) => {
    if (provider === null) return '-';
    if (provider === 'other') return intl.formatMessage({ id: 'life_id.insurance_other' });
    return intl.formatMessage({ id: `life_id.insurance_${provider}` });
  };

  const {
    profile: { sex, birthDate },
  } = patientDetail;

  return (
    <div>
      <div className="bi-list">
        <div className="bi-list__health-data">
          <div>
            <div>
              <span>
                <FormattedMessage id="profile.sex" />:
              </span>
              <span>{renderSex(sex)}</span>
            </div>
            <div>
              <span>
                <FormattedMessage id="profile.birth_number_short" />:
              </span>
              <span>
                {hilbiUserCheck(generalInfo?.userId as string | undefined, () =>
                  renderInsurancePin(patientDetail.profile.pin),
                )}
              </span>
            </div>
          </div>
          <div>
            <div>
              <span>
                <FormattedMessage id="profile.age" />:
              </span>
              <span>{renderAge(birthDate)}</span>
            </div>
            <div>
              <span>
                <FormattedMessage id="profile.blood_type_label" />:
              </span>
              {generalInfo ? (
                <span>
                  {hilbiUserCheck(generalInfo?.userId as string | undefined, () =>
                    renderBloodType(generalInfo.bloodType, generalInfo.rhBloodType),
                  )}
                </span>
              ) : null}
            </div>
          </div>
          <div>
            <div>
              <span>
                <FormattedMessage id="profile.insurance_company_short" />:
              </span>
              <span>
                {hilbiUserCheck(generalInfo?.userId as string | undefined, () =>
                  renderInsuranceProvider(patientDetail.profile.insurance),
                )}
              </span>
            </div>
            <div>
              <span>
                <FormattedMessage id="profile.smoker" />:
              </span>
              {generalInfo ? <span>{renderSmoker(generalInfo?.smoker)}</span> : null}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BaseInfoLifeWrapper;
